import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Tabs,
  Tab,
} from "@mui/material";
import { Bar } from "react-chartjs-2";
import {
  formatMortgageTrackType,
  formatMortgageTrackRepaymentType,
  formatMortgageTrackIndexationType,
  formatMortgageInterestBaseType,
} from "../../utils/mortgageUtil";
import { formatCurrency } from "../../utils/namesFormater";
import { MortgageTrackRepaymentType } from "../../interfaces/DTO/mortgage/mortgage";
import { MortgagePayment } from "../../services/mortgageCalculator";

interface TrackWithAmount {
  track_type: any;
  calculatedAmount: number;
  interest_rate?: number;
  interest_base_type?: any;
  indexation_type?: any;
  interest_update_interval_years?: number;
  amountPercentage?: number;
}

interface MortgageSimulationProps {
  tracksWithAmounts: TrackWithAmount[];
  trackPayments: Map<number, MortgagePayment[]>;
  selectedRepaymentType: MortgageTrackRepaymentType;
  defaultPeriodYears: number;
  startDate?: string; // תאריך התחלת המשכנתא (ISO format: YYYY-MM-DD)
  onPeriodChange: (years: number) => void;
}

const MortgageSimulation: React.FC<MortgageSimulationProps> = ({
  tracksWithAmounts,
  trackPayments,
  selectedRepaymentType,
  defaultPeriodYears,
  startDate,
  onPeriodChange,
}) => {
  const [selectedTrackIndex, setSelectedTrackIndex] = useState<number>(0);
  const [chartViewType, setChartViewType] = useState<"payments" | "remaining">("payments");

  return (
    <Box sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 3, textAlign: "center" }}>
          סימולציה: {formatMortgageTrackRepaymentType(selectedRepaymentType)}
        </Typography>

        {/* טאבים למסלולים */}
        <Tabs
          value={selectedTrackIndex}
          onChange={(_, newValue) => setSelectedTrackIndex(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            mb: 3,
            '& .MuiTab-root.Mui-selected': {
              color: 'secondary.main',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'secondary.main',
            }
          }}
        >
          <Tab label="כל המסלולים" />
          {tracksWithAmounts.map((track, index) => (
            <Tab
              key={index + 1}
              label={formatMortgageTrackType(track.track_type)}
            />
          ))}
        </Tabs>

        {/* תוכן הטאב הנבחר */}
        {selectedTrackIndex === 0 ? (
          // תצוגת כל המסלולים יחד
          <Box>
            {/* בחירת תקופה */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 1, justifyContent: "flex-start" }}>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  תקופת משכנתא (שנים):
                </Typography>
                {[5, 10, 15, 20, 25, 30, 35].map((years) => (
                  <Chip
                    key={years}
                    label={years}
                    onClick={() => onPeriodChange(years)}
                    color={defaultPeriodYears === years ? "primary" : "default"}
                    variant={defaultPeriodYears === years ? "filled" : "outlined"}
                    size="small"
                    sx={{ fontSize: "0.875rem" }}
                  />
                ))}
              </Box>
            </Box>

            {/* כפתורי בחירת סוג תצוגת הגרף */}
            <Box sx={{ display: "flex", gap: 2, mb: 3, justifyContent: "flex-end" }}>
              <Button
                variant={chartViewType === "payments" ? "contained" : "outlined"}
                onClick={() => setChartViewType("payments")}
                size="small"
              >
                החזרים חודשיים
              </Button>
              <Button
                variant={chartViewType === "remaining" ? "contained" : "outlined"}
                onClick={() => setChartViewType("remaining")}
                size="small"
                color="secondary"
              >
                יתרת קרן
              </Button>
            </Box>

            {/* גרף מצטבר של כל המסלולים */}
            {(() => {
              const colors = [
                "rgba(76, 175, 80, 0.8)",    // ירוק
                "rgba(33, 150, 243, 0.8)",   // כחול
                "rgba(255, 152, 0, 0.8)",    // כתום
                "rgba(156, 39, 176, 0.8)",   // סגול
                "rgba(244, 67, 54, 0.8)",    // אדום
                "rgba(0, 188, 212, 0.8)",    // ציאן
              ];

              // בניית datasets לכל המסלולים
              const datasets: any[] = [];

              if (chartViewType === "payments") {
                // תצוגת החזרים חודשיים - כל מסלול בנפרד
                tracksWithAmounts.forEach((track, trackIndex) => {
                  const payments = trackPayments.get(trackIndex) || [];
                  if (payments.length === 0) return;

                  datasets.push({
                    label: `${formatMortgageTrackType(track.track_type)} - תשלום כולל`,
                    data: payments.map((p) => p.totalPayment),
                    backgroundColor: colors[trackIndex % colors.length],
                    stack: "Stack 0",
                  });
                });
              } else {
                // תצוגת יתרת קרן - כל מסלול בנפרד
                tracksWithAmounts.forEach((track, trackIndex) => {
                  const payments = trackPayments.get(trackIndex) || [];
                  if (payments.length === 0) return;

                  datasets.push({
                    label: `${formatMortgageTrackType(track.track_type)} - יתרת קרן`,
                    data: payments.map((p) => p.remainingBalance),
                    backgroundColor: colors[trackIndex % colors.length],
                    stack: "Stack 0",
                  });
                });
              }

              // לוקחים את אורך המקסימלי של חודשים מבין כל המסלולים
              const maxMonths = Math.max(...tracksWithAmounts.map((_, i) => (trackPayments.get(i) || []).length));

              // יצירת תוויות עם שנים
              const labels = Array.from({ length: maxMonths }, (_, i) => {
                if (!startDate) return `חודש ${i + 1}`;
                const date = new Date(startDate);
                date.setMonth(date.getMonth() + i);
                return date.getFullYear().toString();
              });

              const chartData = {
                labels,
                datasets,
              };

              const chartOptions = {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: "top" as const,
                  },
                  tooltip: {
                    callbacks: {
                      label: (context: any) => {
                        return `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`;
                      },
                    },
                  },
                  datalabels: {
                    display: false,
                  },
                },
                scales: {
                  x: {
                    stacked: true,
                    display: true,
                  },
                  y: {
                    stacked: true,
                    ticks: {
                      callback: (value: any) => formatCurrency(value),
                    },
                  },
                },
              };

              return (
                <Box sx={{ height: 400, mb: 4 }}>
                  <Bar data={chartData} options={chartOptions} />
                </Box>
              );
            })()}
          </Box>
        ) : (
          // תצוגת מסלול בודד
          tracksWithAmounts.map((track, index) => {
            if (index + 1 !== selectedTrackIndex) return null;

            const payments = trackPayments.get(index) || [];
            if (payments.length === 0) return null;

            return (
              <Box key={index}>
                {/* בחירת תקופה וריבית */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 1, justifyContent: "flex-start" }}>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      תקופת משכנתא (שנים):
                    </Typography>
                    {[5, 10, 15, 20, 25, 30, 35].map((years) => (
                      <Chip
                        key={years}
                        label={years}
                        onClick={() => onPeriodChange(years)}
                        color={defaultPeriodYears === years ? "primary" : "default"}
                        variant={defaultPeriodYears === years ? "filled" : "outlined"}
                        size="small"
                        sx={{ fontSize: "0.875rem" }}
                      />
                    ))}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ריבית שנתית: {track.interest_rate?.toFixed(2)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1, mt: 0.5 }}>
                    סוג ריבית: {track.interest_base_type ? formatMortgageInterestBaseType(track.interest_base_type) : "לא צוין"} |
                    הצמדה: {track.indexation_type ? formatMortgageTrackIndexationType(track.indexation_type) : "לא צוין"} |
                    עדכון ריבית: {track.interest_update_interval_years ? `כל ${track.interest_update_interval_years} שנים` : "אין"}
                  </Typography>
                </Box>

                {/* כפתורי בחירת סוג תצוגת הגרף */}
                <Box sx={{ display: "flex", gap: 2, mb: 3, justifyContent: "flex-end" }}>
                  <Button
                    variant={chartViewType === "payments" ? "contained" : "outlined"}
                    onClick={() => setChartViewType("payments")}
                    size="small"
                  >
                    החזרים חודשיים
                  </Button>
                  <Button
                    variant={chartViewType === "remaining" ? "contained" : "outlined"}
                    onClick={() => setChartViewType("remaining")}
                    size="small"
                    color="secondary"
                  >
                    יתרת קרן
                  </Button>
                </Box>

                {/* הכנת נתונים לגרף */}
                {(() => {
                // חישוב החודשים שבהם צפוי שינוי ריבית
                const interestUpdateIntervalYears = track.interest_update_interval_years || 0;
                const interestUpdateIntervalMonths = interestUpdateIntervalYears > 0
                  ? Math.round(interestUpdateIntervalYears * 12)
                  : 0;

                // מציאת כל החודשים שבהם צפוי שינוי ריבית
                const interestChangeMonths = new Set<number>();
                if (interestUpdateIntervalMonths > 0) {
                  for (let month = interestUpdateIntervalMonths; month <= defaultPeriodYears * 12; month += interestUpdateIntervalMonths) {
                    interestChangeMonths.add(month);
                  }
                }

                // חישוב הסכום המקורי של המסלול
                const originalAmount = track.calculatedAmount;

                // יצירת datasets בהתאם לסוג התצוגה
                let datasets: any[] = [];

                if (chartViewType === "payments") {
                  // תצוגת החזרים חודשיים
                  datasets = [
                    {
                      label: "תשלום קרן",
                      data: payments.map((p) => p.principalPayment),
                      backgroundColor: "rgba(76, 175, 80, 0.8)", // ירוק כמו ב-LoanCalculator
                      stack: "Stack 0",
                    },
                    {
                      label: "תשלום ריבית",
                      data: payments.map((p) => p.interestPayment),
                      backgroundColor: payments.map((p) =>
                        interestChangeMonths.has(p.month)
                          ? "rgba(200, 30, 70, 1)" // צבע כהה יותר לחודשי השינוי
                          : "rgba(255, 99, 132, 0.8)"
                      ),
                      stack: "Stack 0",
                    },
                  ];
                } else {
                  // תצוגת יתרת קרן - מציגים סכום מלא עם חלוקה ליתרה (אפור בתחתית), קרן ששולמה (ירוק מעל), וגובה ריבית חודשית (ורוד)
                  // חישוב הריבית החודשית על יתרת הקרן (ריבית שנתית / 12)
                  const monthlyInterestRate = (track.interest_rate || 0) / 100 / 12;

                  datasets = [
                    {
                      label: "יתרת קרן",
                      data: payments.map((p) => p.remainingBalance),
                      backgroundColor: payments.map((p) =>
                        interestChangeMonths.has(p.month)
                          ? "rgba(90, 90, 90, 0.8)" // אפור כהה לחודשי השינוי בריבית
                          : "rgba(158, 158, 158, 0.5)" // אפור כמו ב-LoanCalculator - בתחתית
                      ),
                      stack: "Stack 0",
                    },
                    {
                      label: "קרן ששולמה",
                      data: payments.map((p) => originalAmount - p.remainingBalance),
                      backgroundColor: "rgba(76, 175, 80, 0.8)", // ירוק - מעל היתרה
                      stack: "Stack 0",
                    },
                    {
                      label: "ריבית חודשית על יתרת קרן",
                      data: payments.map((p) => p.remainingBalance * monthlyInterestRate),
                      backgroundColor: payments.map((p) =>
                        interestChangeMonths.has(p.month)
                          ? "rgba(200, 30, 70, 1)" // צבע כהה יותר לחודשי השינוי - כמו בגרף החזרים חודשיים
                          : "rgba(255, 99, 132, 0.8)" // ורוד - כמו בגרף החזרים חודשיים
                      ),
                      stack: "Stack 1", // Stack נפרד כדי שיופיע מעל הקרן
                    },
                  ];
                }

                // יצירת תוויות עם שנים
                const labels = payments.map((p) => {
                  if (!startDate) return `חודש ${p.month}`;
                  const date = new Date(startDate);
                  date.setMonth(date.getMonth() + p.month - 1);
                  return date.getFullYear().toString();
                });

                const chartData = {
                  labels,
                  datasets,
                };

                const chartOptions = {
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: "top" as const,
                    },
                    tooltip: {
                      callbacks: {
                        label: (context: any) => {
                          return `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`;
                        },
                      },
                    },
                    datalabels: {
                      display: false,
                    },
                  },
                  scales: {
                    x: {
                      stacked: true,
                      display: true,
                    },
                    y: {
                      stacked: true,
                      ticks: {
                        callback: (value: any) => formatCurrency(value),
                      },
                    },
                  },
                };

                return (
                  <>
                    {/* גרף */}
                    <Box sx={{ height: 400, mb: 4 }}>
                      <Bar data={chartData} options={chartOptions} />
                    </Box>

                    {/* טבלה */}
                    <Box sx={{ overflowX: "auto" }}>
                      {interestChangeMonths.size > 0 && (
                        <Box sx={{ mb: 2, p: 2, bgcolor: chartViewType === "remaining" ? "rgba(90, 90, 90, 0.1)" : "rgba(200, 30, 70, 0.1)", borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                bgcolor: chartViewType === "remaining" ? "rgba(90, 90, 90, 0.8)" : "rgba(200, 30, 70, 1)",
                                borderRadius: 0.5,
                              }}
                            />
                            <span>
                              שורות מודגשות מסמלות חודשים בהם צפוי שינוי בריבית (כל {interestUpdateIntervalYears} שנים):
                              {" "}
                              {Array.from(interestChangeMonths).sort((a, b) => a - b).join(", ")}
                            </span>
                          </Typography>
                        </Box>
                      )}
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ backgroundColor: "#f5f5f5" }}>
                            <th style={{ padding: "12px", textAlign: "right", border: "1px solid #ddd" }}>חודש</th>
                            <th style={{ padding: "12px", textAlign: "right", border: "1px solid #ddd" }}>תשלום קרן</th>
                            <th style={{ padding: "12px", textAlign: "right", border: "1px solid #ddd" }}>תשלום ריבית</th>
                            <th style={{ padding: "12px", textAlign: "right", border: "1px solid #ddd" }}>תשלום כולל</th>
                            <th style={{ padding: "12px", textAlign: "right", border: "1px solid #ddd" }}>יתרת קרן</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payments.map((payment) => {
                            const isInterestChangeMonth = interestChangeMonths.has(payment.month);
                            return (
                              <tr
                                key={payment.month}
                                style={{
                                  backgroundColor: isInterestChangeMonth ? "rgba(200, 30, 70, 0.15)" : "transparent",
                                  fontWeight: isInterestChangeMonth ? "bold" : "normal",
                                }}
                              >
                                <td style={{ padding: "12px", textAlign: "right", border: "1px solid #ddd" }}>
                                  {payment.month}
                                </td>
                                <td style={{ padding: "12px", textAlign: "right", border: "1px solid #ddd" }}>
                                  {formatCurrency(payment.principalPayment)}
                                </td>
                                <td style={{ padding: "12px", textAlign: "right", border: "1px solid #ddd" }}>
                                  {formatCurrency(payment.interestPayment)}
                                </td>
                                <td style={{ padding: "12px", textAlign: "right", border: "1px solid #ddd" }}>
                                  {formatCurrency(payment.totalPayment)}
                                </td>
                                <td style={{ padding: "12px", textAlign: "right", border: "1px solid #ddd" }}>
                                  {formatCurrency(payment.remainingBalance)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </Box>
                  </>
                );
              })()}
              </Box>
            );
          })
        )}
      </Paper>
    </Box>
  );
};

export default MortgageSimulation;
